import { sha256 } from '@noble/hashes/sha256';
import { sha512 } from '@noble/hashes/sha512';
import { bytesToHex } from '@noble/hashes/utils';
import { AttestationError } from './errors.js';
import type { AttestationPolicy } from './types.js';

const TEE_TYPE_TDX = 0x00000081;
const INTEL_QUOTE_HEADER_SIZE = 48;
const BODY_HEADER_SIZE = 6;
const TDX_QUOTE_BODY_10_SIZE = 584;

/** Parsed TDX attestation quote. */
export interface AttestationQuote {
  version: number;
  teeType: number;
  reportData: Uint8Array; // 64 bytes
  mrTd: Uint8Array; // 48 bytes
  mrConfigId: Uint8Array; // 48 bytes
  mrOwner: Uint8Array; // 48 bytes
  mrOwnerConfig: Uint8Array; // 48 bytes
  rtmr: [Uint8Array, Uint8Array, Uint8Array, Uint8Array]; // 4 × 48 bytes
}

/** Compute cocoon image hash: SHA-256(mr_td || mr_config_id || mr_owner || mr_owner_config || rtmr[0..3] || zeros[64]) */
export function imageHash(quote: AttestationQuote): Uint8Array {
  const buf = new Uint8Array(48 * 8 + 64); // 4 MR fields + 4 RTMR + 64 zeros
  let off = 0;
  for (const field of [quote.mrTd, quote.mrConfigId, quote.mrOwner, quote.mrOwnerConfig, ...quote.rtmr]) {
    buf.set(field, off);
    off += 48;
  }
  // Last 64 bytes are already zeros
  return sha256(buf);
}

/** Read uint16 LE from data at offset. */
function readU16LE(data: Uint8Array, off: number): number {
  return data[off] | (data[off + 1] << 8);
}

/** Read uint32 LE from data at offset. */
function readU32LE(data: Uint8Array, off: number): number {
  return data[off] | (data[off + 1] << 8) | (data[off + 2] << 16) | (data[off + 3] << 24);
}

/** Parse a TDX attestation quote. Supports Intel TDX real quotes and mock quotes. */
export function parseQuote(data: Uint8Array): AttestationQuote {
  if (data.length < 48) {
    throw new AttestationError(`Attestation quote too short: ${data.length} bytes`);
  }

  const version16 = readU16LE(data, 0);
  if (version16 >= 3 && version16 <= 5 && data.length >= INTEL_QUOTE_HEADER_SIZE + TDX_QUOTE_BODY_10_SIZE) {
    return parseIntelTDXQuote(data);
  }

  return parseMockQuote(data);
}

function parseIntelTDXQuote(data: Uint8Array): AttestationQuote {
  const version = readU16LE(data, 0);
  const teeType = readU32LE(data, 4) >>> 0; // unsigned

  if (teeType !== TEE_TYPE_TDX) {
    throw new AttestationError(`Not a TDX quote: TEE type 0x${teeType.toString(16).padStart(8, '0')}`);
  }

  let off = INTEL_QUOTE_HEADER_SIZE;
  if (version === 5) {
    off += BODY_HEADER_SIZE;
  }

  if (data.length < off + TDX_QUOTE_BODY_10_SIZE) {
    throw new AttestationError(`Quote too short for TDX body: ${data.length} bytes`);
  }

  // Skip: tee_tcb_svn(16) + mr_seam(48) + mr_signer_seam(48) + seam_attributes(8) + td_attributes(8) + xfam(8)
  off += 16 + 48 + 48 + 8 + 8 + 8;

  const mrTd = data.slice(off, off + 48); off += 48;
  const mrConfigId = data.slice(off, off + 48); off += 48;
  const mrOwner = data.slice(off, off + 48); off += 48;
  const mrOwnerConfig = data.slice(off, off + 48); off += 48;
  const rtmr: [Uint8Array, Uint8Array, Uint8Array, Uint8Array] = [
    data.slice(off, off + 48),
    data.slice(off + 48, off + 96),
    data.slice(off + 96, off + 144),
    data.slice(off + 144, off + 192),
  ];
  off += 192;
  const reportData = data.slice(off, off + 64);

  return { version, teeType, reportData, mrTd, mrConfigId, mrOwner, mrOwnerConfig, rtmr };
}

function parseMockQuote(data: Uint8Array): AttestationQuote {
  const mockMinSize = 4 + 2 + 64 + 48 + 48 * 4;
  if (data.length < mockMinSize) {
    throw new AttestationError(`Mock quote too short: ${data.length} bytes, need ${mockMinSize}`);
  }

  const version = readU16LE(data, 0);
  const teeType = readU16LE(data, 4);
  const reportData = data.slice(6, 70);
  const mrTd = data.slice(70, 118);
  const rtmr: [Uint8Array, Uint8Array, Uint8Array, Uint8Array] = [
    data.slice(118, 166),
    data.slice(166, 214),
    data.slice(214, 262),
    data.slice(262, 310),
  ];

  return {
    version,
    teeType,
    reportData,
    mrTd,
    mrConfigId: new Uint8Array(48),
    mrOwner: new Uint8Array(48),
    mrOwnerConfig: new Uint8Array(48),
    rtmr,
  };
}

/** Verify report_data binds the proxy's Ed25519 public key: report_data == SHA-512(pubkey). */
export function verifyReportData(quote: AttestationQuote, proxyPubKey: Uint8Array): void {
  if (proxyPubKey.length !== 32) {
    throw new AttestationError(`Invalid proxy public key size: ${proxyPubKey.length}`);
  }
  const expected = sha512(proxyPubKey);
  if (quote.reportData.length !== expected.length) {
    throw new AttestationError('report_data length mismatch');
  }
  for (let i = 0; i < expected.length; i++) {
    if (quote.reportData[i] !== expected[i]) {
      throw new AttestationError('Attestation report_data does not match proxy public key (SHA-512)');
    }
  }
}

/** Known cocoon proxy image hashes from the root contract on TON mainnet. */
export const ALLOWED_COCOON_PROXY_IMAGE_HASHES = [
  'c4f99569acaa71ae2f6b091b64ff6645b97eb7ab3d8c463dc2d7be752212008d',
];

/** Returns a default attestation policy using known image hashes. */
export function defaultAttestationPolicy(): AttestationPolicy {
  return {
    allowedCocoonProxyImageHashes: [...ALLOWED_COCOON_PROXY_IMAGE_HASHES],
  };
}

/** Verify an attestation quote against a policy. */
export function verifyPolicy(
  quoteBytes: Uint8Array,
  proxyPubKey: Uint8Array | null,
  policy: AttestationPolicy,
): AttestationQuote {
  const quote = parseQuote(quoteBytes);

  if (quote.teeType !== TEE_TYPE_TDX) {
    throw new AttestationError(
      `Unexpected TEE type: 0x${quote.teeType.toString(16).padStart(8, '0')}, expected 0x${TEE_TYPE_TDX.toString(16).padStart(8, '0')} (TDX)`,
    );
  }

  if (proxyPubKey && proxyPubKey.length > 0) {
    verifyReportData(quote, proxyPubKey);
  }

  if (policy.allowedCocoonProxyImageHashes.length > 0) {
    const imgHash = bytesToHex(imageHash(quote));
    if (!policy.allowedCocoonProxyImageHashes.includes(imgHash)) {
      throw new AttestationError(`Image hash ${imgHash} not in allowed list`);
    }
  }

  return quote;
}
